import React, { useState } from 'react';

import { getUserL2Access } from 'Deer/helpers/getUserAccess';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useLocation } from 'react-router-dom';
import { useCreateObjectivePanel } from 'reducers/createObjectivePanelSlice';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import {
  checkFeedbackModuleEnabled,
  checkGoalModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkReviewModuleEnabled,
  checkTaskModuleEnabled,
} from 'utils/helpers/moduleAccess';

import {
  FeedbackIconDualTone,
  FeedbackIconSelected,
  GoalIconDualTone,
  GoalIconSelected,
  InitiativeV1,
  LevelUpIconDualTone,
  LevelUpIconSelected,
  OneOnOneIconDualTone,
  OneOnOneIconSelected,
  ReviewsIconDualTone,
  ReviewsIconSelected,
} from 'assets/assetsV1/iconComponents';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendClickedOnModuleDataEvent } from 'constants/mixpanelHelper';

import { NavigationSidebar } from 'uiToolkitV1/NavigationSidebar';

import { useOneOnOneInstanceDetails } from '../../hooks/dataHooks';
import { CompetencySection } from './features/CompetencySection';
import { FeedbackSection } from './features/FeedbackSection';
import { GoalsSection } from './features/Goals/GoalsSection';
import { InitiativesSection } from './features/InitiativesSection';
import { OneOnOneSection } from './features/OneOnOne/OneOnOneSection';
import { ReviewsSection } from './features/ReviewsSection';

interface Props {
  setIsExpanded: (isExpanded: boolean) => void;
  isExpanded: boolean;
  isBotNotificationBarOpen?: boolean;
  wrapperRef: React.RefObject<HTMLDivElement>;
}

const SIDEBAR_OPTIONS = [
  {
    label: 'Past 1:1s',
    Icon: OneOnOneIconDualTone,
    HoverIcon: OneOnOneIconSelected,
    onClick: ({ instanceId }: { instanceId: number }) => {
      sendClickedOnModuleDataEvent({
        eventProperties: {
          type: 'Past 1:1s',
          instance_id: instanceId,
          page: '1:1 instance page',
        },
      });
    },
    isEnabled: checkOneOnOneModuleEnabled,
    RenderComponent: OneOnOneSection,
    showForRelationTypes: ['MANAGER', 'SKIP_MANAGER', 'PEER'],
  },
  {
    label: 'Goals',
    Icon: GoalIconDualTone,
    HoverIcon: GoalIconSelected,
    onClick: ({ instanceId }: { instanceId: number }) => {
      sendClickedOnModuleDataEvent({
        eventProperties: {
          type: 'Goals',
          instance_id: instanceId,
          page: '1:1 instance page',
        },
      });
    },
    isEnabled: checkGoalModuleEnabled,
    RenderComponent: GoalsSection,
    showForRelationTypes: ['MANAGER', 'SKIP_MANAGER'],
  },
  {
    label: 'Competencies',
    Icon: LevelUpIconDualTone,
    HoverIcon: LevelUpIconSelected,
    onClick: ({ instanceId }: { instanceId: number }) => {
      sendClickedOnModuleDataEvent({
        eventProperties: {
          type: 'Competency',
          instance_id: instanceId,
          page: '1:1 instance page',
        },
      });
    },
    isEnabled: () => checkFeedbackModuleEnabled() && getUserL2Access().hasIDPAccess,
    RenderComponent: CompetencySection,
    showForRelationTypes: ['MANAGER', 'SKIP_MANAGER'],
  },
  {
    label: 'Feedback',
    Icon: FeedbackIconDualTone,
    HoverIcon: FeedbackIconSelected,
    onClick: ({ instanceId }: { instanceId: number }) => {
      sendClickedOnModuleDataEvent({
        eventProperties: {
          type: 'Feedback',
          instance_id: instanceId,
          page: '1:1 instance page',
        },
      });
    },
    isEnabled: checkFeedbackModuleEnabled,
    RenderComponent: FeedbackSection,
    showForRelationTypes: ['MANAGER', 'SKIP_MANAGER'],
  },

  {
    label: 'Reviews',
    Icon: ReviewsIconDualTone,
    HoverIcon: ReviewsIconSelected,
    onClick: ({ instanceId }: { instanceId: number }) => {
      sendClickedOnModuleDataEvent({
        eventProperties: {
          type: 'Reviews',
          instance_id: instanceId,
          page: '1:1 instance page',
        },
      });
    },
    isEnabled: checkReviewModuleEnabled,
    RenderComponent: ReviewsSection,
    showForRelationTypes: ['MANAGER', 'SKIP_MANAGER'],
  },
  {
    label: 'Initiatives',
    Icon: InitiativeV1,
    HoverIcon: InitiativeV1,
    onClick: ({ instanceId }: { instanceId: number }) => {
      sendClickedOnModuleDataEvent({
        eventProperties: {
          type: 'Initiatives',
          instance_id: instanceId,
          page: '1:1 instance page',
        },
      });
    },
    isEnabled: checkTaskModuleEnabled,
    RenderComponent: InitiativesSection,
    showForRelationTypes: ['MANAGER', 'SKIP_MANAGER'],
  },
];

const RightSidebar = ({ setIsExpanded, isExpanded, isBotNotificationBarOpen, wrapperRef }: Props) => {
  const { masterId, instanceId } = useOneOnOnePageParams();
  const { moduleName } = useOrganisationSettings();

  const location = useLocation();

  const { search } = location;

  const [selectedOption, setSelectedOption] = useState('');

  // OKR Sidepanel
  const { isOpen } = useCreateObjectivePanel();

  const {
    receiverEmployee: { displayName },
    relationType,
    employeeDetails: { displayName: employeeName },
  } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const validSidebarOptions = SIDEBAR_OPTIONS.filter(
    (option) => option.isEnabled() && option.showForRelationTypes.includes(relationType)
  );
  const goalsSidebarOption = validSidebarOptions.find((option) => option.label === 'Goals');
  if (goalsSidebarOption?.label) {
    goalsSidebarOption.label = modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE);
  }

  // If there are no valid sidebar options, don't render the sidebar or if the relation type is loading
  if (!relationType) {
    return null;
  }

  return (
    <NavigationSidebar
      menuItems={validSidebarOptions}
      sx={{
        position: 'fixed',
        bottom: '80px',
        // maxHeight: !isExpanded ? validSidebarOptions.length * 90 : validSidebarOptions.length * 100
        height: '80vh',
        '.nav-sidebar-buttons': {
          height: '80vh',
          background: '#F5F7F9',
          padding: '4px',
          borderRadius: isExpanded ? '0px' : '8px 0px 0px 8px',
          overflowY: 'auto',
        },
      }}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      heading={selectedOption === 'Past 1:1s' ? `1:1 with ${employeeName}` : `${displayName}'s Progress`}
      // using outside click only if there's no other sidepanel open on top of right sidebar which is controlled from query params or search in route
      closeOnOutsideClick={search === '' || !isOpen}
      setSelectedOption={setSelectedOption}
      wrapperRef={wrapperRef}
    />
  );
};

export { RightSidebar };
